import React, { useState } from "react";
import {
  Modal,
  List,
  Button,
  Tag,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Divider,
  message,
  Spin,
} from "antd";
import styled from "styled-components";
import {
  WarningOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  HomeOutlined,
  MessageOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { trackEvent } from "../helpers/mixpanel";
import { exportDelinquencyToExcel } from "../utils/excelExport";
import {
  sendAllOverdueReminders,
  sendOverdueReminderToTenant,
  sendIndividualReminder,
  handleReminderError,
  formatReminderSuccessMessage,
} from "../lib/api/rentReminderAPI";

const { Title, Text } = Typography;

const DelinquencyReportModal = ({
  isOpen,
  onClose,
  overduePayments = [],
  totalDelinquent = 0,
  onSendReminder,
  onMarkPaid,
  onGenerateStatement,
  landlordId,
}) => {
  const [isSendingOverdueReminders, setIsSendingOverdueReminders] =
    useState(false);
  const [sendingReminderToTenant, setSendingReminderToTenant] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleSendReminder = async (payment) => {
    if (onSendReminder) {
      await onSendReminder(payment);
    } else {
      // Fallback to new API
      try {
        setSendingReminderToTenant(payment.tenantId);
        const result = await sendIndividualReminder(
          payment.tenantId,
          landlordId,
          "rent_due"
        );
        message.success(formatReminderSuccessMessage(result, "individual"));
        trackEvent("rent_reminder_sent", {
          payment_id: payment.id,
          tenant_id: payment.tenantId,
          reminder_type: "rent_due",
        });
      } catch (error) {
        handleReminderError(error, message.error, message.warning);
      } finally {
        setSendingReminderToTenant(null);
      }
    }
  };

  const handleSendOverdueReminder = async (payment) => {
    try {
      setSendingReminderToTenant(payment.tenantId);
      const customMessage = `This is a reminder that your rent payment is overdue. Please contact us immediately to discuss payment arrangements.`;
      const result = await sendOverdueReminderToTenant(
        payment.tenantId,
        landlordId,
        customMessage,
        true // include late fee
      );
      message.success(formatReminderSuccessMessage(result, "individual"));
      trackEvent("overdue_reminder_sent", {
        payment_id: payment.id,
        tenant_id: payment.tenantId,
        reminder_type: "overdue_with_late_fee",
      });
    } catch (error) {
      handleReminderError(error, message.error, message.warning);
    } finally {
      setSendingReminderToTenant(null);
    }
  };

  const handleSendAllOverdueReminders = async () => {
    try {
      setIsSendingOverdueReminders(true);
      const result = await sendAllOverdueReminders(landlordId, true);
      message.success(formatReminderSuccessMessage(result, "overdue"));
      trackEvent("bulk_overdue_reminders_sent", {
        landlord_id: landlordId,
        total_sent: result.data?.sent || 0,
        total_failed: result.data?.failed || 0,
      });
    } catch (error) {
      handleReminderError(error, message.error, message.warning);
    } finally {
      setIsSendingOverdueReminders(false);
    }
  };

  const handleMarkPaid = (payment) => {
    if (onMarkPaid) {
      return onMarkPaid(payment);
    }
    return undefined;
  };

  const showActionConfirmation = ({
    content,
    okText,
    okType,
    onConfirm,
  }) => {
    Modal.confirm({
      centered: true,
      title: null,
      icon: null,
      content: (
        <ActionConfirmationContent>
          <ExclamationCircleOutlined aria-label="Confirmation required" />
          <ActionConfirmationMessage>{content}</ActionConfirmationMessage>
        </ActionConfirmationContent>
      ),
      okText,
      okType,
      cancelText: "Cancel",
      onOk: onConfirm,
      styles: {
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.72)",
        },
      },
    });
  };

  const confirmSendReminder = (payment) => {
    const tenantName = payment.tenantName || "this tenant";
    showActionConfirmation({
      content: `Send a regular rent reminder to ${tenantName}?`,
      okText: "Send Reminder",
      onConfirm: () => handleSendReminder(payment),
    });
  };

  const confirmSendOverdueReminder = (payment) => {
    const tenantName = payment.tenantName || "this tenant";
    showActionConfirmation({
      content: `Send an overdue rent reminder, including late-fee information, to ${tenantName}?`,
      okText: "Send Overdue Reminder",
      okType: "danger",
      onConfirm: () => handleSendOverdueReminder(payment),
    });
  };

  const confirmMarkPaid = (payment) => {
    const tenantName = payment.tenantName || "this tenant";
    const amount = Number(payment.amount || 0).toLocaleString();
    showActionConfirmation({
      content: `Mark the $${amount} payment for ${tenantName} as paid?`,
      okText: "Mark Paid",
      onConfirm: () => handleMarkPaid(payment),
    });
  };

  const handleGenerateStatement = async () => {
    // Always generate the PDF statement
    await generateDelinquencyStatement();

    // Also call the prop callback if provided (for analytics/tracking)
    if (onGenerateStatement) {
      onGenerateStatement();
    }
  };

  const handleExportToExcel = async () => {
    try {
      setIsGenerating(true);

      // Transform overdue payment data to match Excel export format
      const delinquencyData = (overduePayments || []).map((payment) => {
        const daysOverdue = getDaysOverdue(payment.dueDate);
        return {
          propertyName: payment.propertyName || "N/A",
          unitNumber: payment.unitName || payment.unitNumber || "N/A",
          tenantName: payment.tenantName || "Unknown",
          tenantEmail: payment.tenantEmail || "",
          rentAmount: payment.amount || 0,
          daysOverdue: daysOverdue,
          lateFees: payment.lateFee || 0,
          totalDue: (payment.amount || 0) + (payment.lateFee || 0),
          lastContact: payment.lastContact
            ? payment.lastContact.toDate
              ? payment.lastContact.toDate()
              : new Date(payment.lastContact)
            : null,
          contactMethod: payment.contactMethod || "",
          notes: payment.notes || "",
        };
      });

      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
      await exportDelinquencyToExcel(delinquencyData, currentMonth);

      message.success("Delinquency report exported to Excel successfully!");

      trackEvent("delinquency_excel_exported", {
        overdue_count: (overduePayments || []).length,
        total_delinquent: totalDelinquent,
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error("Failed to export to Excel. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const generateDelinquencyStatement = async () => {
    setIsGenerating(true);
    try {
      // Calculate comprehensive metrics
      const paymentsList = overduePayments || [];
      const daysOverdueList = paymentsList.map((p) =>
        getDaysOverdue(p.dueDate)
      );
      const averageDaysOverdue =
        daysOverdueList.length > 0
          ? Math.round(
              daysOverdueList.reduce((sum, d) => sum + d, 0) /
                daysOverdueList.length
            )
          : 0;

      const criticalPayments = paymentsList.filter(
        (p) => getDaysOverdue(p.dueDate) >= 30
      ).length;
      const highPriorityPayments = paymentsList.filter((p) => {
        const days = getDaysOverdue(p.dueDate);
        return days >= 14 && days < 30;
      }).length;
      const moderatePayments = paymentsList.filter(
        (p) => getDaysOverdue(p.dueDate) < 14
      ).length;

      const oldestOverdue =
        daysOverdueList.length > 0 ? Math.max(...daysOverdueList) : 0;
      const newestOverdue =
        daysOverdueList.length > 0
          ? Math.min(...daysOverdueList.filter((d) => d > 0))
          : 0;

      // Calculate potential late fees (estimate 5% per month)
      const estimatedLateFees = paymentsList.reduce((sum, p) => {
        const days = getDaysOverdue(p.dueDate);
        const monthsOverdue = Math.ceil(days / 30);
        return sum + (p.amount || 0) * 0.05 * monthsOverdue;
      }, 0);

      // Create a comprehensive delinquency statement
      const statementData = {
        title: "Delinquency Report",
        generatedDate: new Date().toLocaleDateString(),
        totalOutstanding: totalDelinquent,
        overdueAccounts: paymentsList.length,
        averageDaysOverdue: averageDaysOverdue,
        criticalPayments: criticalPayments,
        highPriorityPayments: highPriorityPayments,
        moderatePayments: moderatePayments,
        oldestOverdue: oldestOverdue,
        newestOverdue: newestOverdue,
        estimatedLateFees: estimatedLateFees,
        totalWithFees: totalDelinquent + estimatedLateFees,
        payments: paymentsList.map((payment) => ({
          tenantName: payment.tenantName || "Unknown Tenant",
          unitName: payment.unitName || "Unknown Door",
          amount: payment.amount || 0,
          dueDate:
            payment.dueDate?.toDate?.()?.toLocaleDateString() || "Unknown Date",
          daysOverdue: getDaysOverdue(payment.dueDate),
          severity: getSeverityText(getDaysOverdue(payment.dueDate)),
        })),
      };

      // Generate PDF content
      const pdfContent = generatePDFContent(statementData);

      // Create and download the PDF
      await downloadPDF(
        pdfContent,
        `delinquency-report-${new Date().toISOString().split("T")[0]}.pdf`
      );

      // Track the event
      try {
        trackEvent("delinquency_statement_generated", {
          totalOutstanding: totalDelinquent,
          overdueAccounts: (overduePayments || []).length,
          statementType: "legal_delinquency_report",
        });
      } catch (error) {
        console.warn(
          "Failed to track delinquency statement generation:",
          error
        );
      }
    } catch (error) {
      console.error("Error generating delinquency statement:", error);
      message.error(
        "Failed to generate delinquency statement. Please try again."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePDFContent = (data) => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${data.title}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Times New Roman', Times, serif;
            margin: 0;
            padding: 0;
            color: #000000;
            line-height: 1.6;
            background: #ffffff;
            font-size: 11pt;
          }
          .report-container {
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0.75in;
          }
          .report-header {
            border-bottom: 3px solid #dc3545;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .report-title {
            font-size: 24pt;
            font-weight: bold;
            color: #dc3545;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .report-meta {
            font-size: 10pt;
            color: #666666;
            margin-top: 8px;
          }
          .executive-summary {
            margin-bottom: 35px;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 14pt;
            font-weight: bold;
            color: #dc3545;
            margin-bottom: 15px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border-bottom: 2px solid #dc3545;
            padding-bottom: 5px;
          }
          .summary-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          .summary-table td {
            padding: 12px 15px;
            border: 1px solid #cccccc;
            font-size: 10pt;
          }
          .summary-table td:first-child {
            font-weight: bold;
            background-color: #f5f5f5;
            width: 40%;
            color: #333333;
          }
          .summary-table td:last-child {
            text-align: right;
            font-weight: bold;
            color: #dc3545;
            font-size: 11pt;
          }
          .payments-section {
            margin-top: 12rem;
            page-break-inside: avoid;
            page-break-before: always;
          }
          .payments-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
            font-size: 9pt;
            table-layout: fixed;
            word-wrap: break-word;
          }
          .payments-table thead {
            background-color: #ffffff;
            border-bottom: 2px solid #dc3545;
          }
          .payments-table th {
            padding: 10px 6px;
            text-align: left;
            font-weight: bold;
            font-size: 8pt;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #dc3545;
            border: 1px solid #dc3545;
            background-color: #ffffff;
          }
          .payments-table th:nth-child(1) { width: 20%; }
          .payments-table th:nth-child(2) { width: 15%; }
          .payments-table th:nth-child(3) { width: 12%; }
          .payments-table th:nth-child(4) { width: 12%; }
          .payments-table th:nth-child(5) { width: 15%; }
          .payments-table th:nth-child(6) { width: 13%; }
          .payments-table th:nth-child(7) { width: 13%; }
          .payments-table th:nth-child(4),
          .payments-table th:nth-child(5),
          .payments-table th:nth-child(6) {
            text-align: center;
          }
          .payments-table th:last-child {
            text-align: right;
          }
          .payments-table td {
            padding: 8px 6px;
            border: 1px solid #cccccc;
            vertical-align: top;
            font-size: 9pt;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          .payments-table tbody tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .payments-table td:last-child {
            text-align: right;
            font-weight: bold;
            color: #dc3545;
          }
          .payments-table td:nth-child(4),
          .payments-table td:nth-child(5),
          .payments-table td:nth-child(6) {
            text-align: center;
            font-size: 8pt;
          }
          .severity-critical {
            color: #721c24;
            font-weight: bold;
          }
          .severity-high {
            color: #856404;
            font-weight: bold;
          }
          .severity-moderate {
            color: #0c5460;
            font-weight: bold;
          }
          .footer-section {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #cccccc;
            font-size: 9pt;
            color: #666666;
            text-align: center;
            page-break-inside: avoid;
          }
          .footer-section p {
            margin: 5px 0;
          }
          .disclaimer {
            margin-top: 30px;
            padding: 15px;
            background-color: #fff3cd;
            border-left: 4px solid #dc3545;
            font-size: 9pt;
            color: #856404;
            line-height: 1.5;
            page-break-inside: avoid;
          }
          .disclaimer-title {
            font-weight: bold;
            margin-bottom: 5px;
            color: #721c24;
          }
          @media print {
            .report-container {
              padding: 0.5in;
            }
            .payments-table tbody tr {
              page-break-inside: avoid;
            }
            .payments-section {
              page-break-after: avoid;
            }
            .disclaimer {
              page-break-before: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <div class="report-header">
            <div class="report-title">${data.title}</div>
            <div class="report-meta">
              <div>Report Date: ${data.generatedDate}</div>
              <div>Generated by: LandlordOps</div>
            </div>
          </div>

          <div class="executive-summary">
            <div class="section-title">Executive Summary</div>
            
            <div style="margin-bottom: 25px;">
              <div style="font-size: 11pt; font-weight: bold; color: #dc3545; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 0.5px;">Delinquency Overview</div>
              <table class="summary-table">
                <tr>
                  <td>Total Outstanding Amount</td>
                  <td>$${data.totalOutstanding.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Overdue Accounts</td>
                  <td>${data.overdueAccounts}</td>
                </tr>
                <tr>
                  <td>Average Days Overdue</td>
                  <td>${data.averageDaysOverdue} days</td>
                </tr>
                <tr>
                  <td>Oldest Overdue Payment</td>
                  <td>${data.oldestOverdue} days</td>
                </tr>
                <tr>
                  <td>Newest Overdue Payment</td>
                  <td>${data.newestOverdue} days</td>
                </tr>
              </table>
            </div>

            <div style="margin-bottom: 25px;">
              <div style="font-size: 11pt; font-weight: bold; color: #dc3545; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 0.5px;">Priority Breakdown</div>
              <table class="summary-table">
                <tr>
                  <td>Critical (30+ days overdue)</td>
                  <td>${data.criticalPayments}</td>
                </tr>
                <tr>
                  <td>High Priority (14-29 days overdue)</td>
                  <td>${data.highPriorityPayments}</td>
                </tr>
                <tr>
                  <td>Moderate (1-13 days overdue)</td>
                  <td>${data.moderatePayments}</td>
                </tr>
              </table>
            </div>

            <div>
              <div style="font-size: 11pt; font-weight: bold; color: #dc3545; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 0.5px;">Financial Impact</div>
              <table class="summary-table">
                <tr>
                  <td>Base Outstanding Amount</td>
                  <td>$${data.totalOutstanding.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Estimated Late Fees (5% per month)</td>
                  <td>$${data.estimatedLateFees.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Total Potential Collection</td>
                  <td>$${data.totalWithFees.toFixed(2)}</td>
                </tr>
              </table>
            </div>
          </div>

          <div class="payments-section">
            <div class="section-title">Overdue Payment Details</div>
            ${
              data.payments.length > 0
                ? `
              <table class="payments-table">
                <thead>
                  <tr>
                    <th>Tenant Name</th>
                    <th>Unit</th>
                    <th>Due Date</th>
                    <th>Days Overdue</th>
                    <th>Severity</th>
                    <th>Amount Due</th>
                    <th>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  ${data.payments
                    .map(
                      (payment) => `
                    <tr>
                      <td><strong>${payment.tenantName}</strong></td>
                      <td>${payment.unitName}</td>
                      <td>${payment.dueDate}</td>
                      <td>${payment.daysOverdue}</td>
                      <td><span class="severity-${payment.severity.toLowerCase()}">${
                        payment.severity
                      }</span></td>
                      <td>$${payment.amount.toLocaleString()}</td>
                      <td>$${payment.amount.toLocaleString()}</td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
              </table>
            `
                : `
              <div style="text-align: center; padding: 40px; color: #666666; font-size: 11pt;">
                No overdue payments found. All accounts are current.
              </div>
            `
            }
          </div>

          <div class="disclaimer">
            <div class="disclaimer-title">Legal Notice</div>
            <div>
              This delinquency report is generated for legal and collection purposes as of ${
                data.generatedDate
              }. 
              All amounts listed represent outstanding rent payments as of the report date. Estimated late fees 
              are calculated at 5% per month and may vary based on lease terms. This document may be used as 
              evidence in legal proceedings related to rent collection and property management disputes. 
              For questions regarding this report, please contact your property management team.
            </div>
          </div>

          <div class="footer-section">
            <div>This document was generated by LandlordOps</div>
            <div style="margin-top: 5px;">© ${new Date().getFullYear()} LandlordOps. All rights reserved.</div>
          </div>
        </div>
      </body>
      </html>
    `;

    return htmlContent;
  };

  const downloadPDF = async (htmlContent, filename) => {
    try {
      // Create a temporary container element with proper styling
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.top = "0";
      tempDiv.style.width = "210mm"; // A4 width
      tempDiv.style.backgroundColor = "#ffffff";
      tempDiv.style.padding = "0";
      tempDiv.style.margin = "0";
      tempDiv.innerHTML = htmlContent;
      document.body.appendChild(tempDiv);

      // Wait a bit for styles to apply
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Wait for images to load
      await new Promise((resolve) => {
        const images = tempDiv.getElementsByTagName("img");
        let loadedImages = 0;
        const totalImages = images.length;

        if (totalImages === 0) {
          resolve();
          return;
        }

        const imageLoaded = () => {
          loadedImages++;
          if (loadedImages === totalImages) {
            resolve();
          }
        };

        Array.from(images).forEach((img) => {
          if (img.complete) {
            imageLoaded();
          } else {
            img.onload = imageLoaded;
            img.onerror = imageLoaded;
          }
        });
      });

      // Convert HTML to canvas with optimized settings for smaller file size
      const canvas = await html2canvas(tempDiv, {
        scale: 1.5, // Reduced from 2 to 1.5 for smaller file size (still good quality)
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        width: tempDiv.scrollWidth,
        height: tempDiv.scrollHeight,
        windowWidth: tempDiv.scrollWidth,
        windowHeight: tempDiv.scrollHeight,
        allowTaint: false,
        foreignObjectRendering: false,
      });

      // Remove temporary element
      document.body.removeChild(tempDiv);

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF("p", "mm", "a4");

      // Add pages if content is taller than one page
      const pageHeight = 297; // A4 height in mm
      const pageWidth = 210; // A4 width in mm
      let heightLeft = imgHeight;
      let position = 0;

      // Use JPEG format with compression for much smaller file size
      // Quality 0.85 provides good balance between quality and file size
      const imageData = canvas.toDataURL("image/jpeg", 0.85);
      // Add first page
      pdf.addImage(imageData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imageData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      pdf.save(filename);

      // Show success message
      message.success(
        "Delinquency report generated and downloaded successfully!"
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF. Please try again.");
      throw error;
    }
  };

  const getDaysOverdue = (dueDate) => {
    if (dueDate == null || dueDate === undefined) return 0;
    const due = dueDate?.toDate?.() || new Date(dueDate);
    if (Number.isNaN(due.getTime())) return 0;
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getSeverityColor = (daysOverdue) => {
    if (daysOverdue >= 30) return "error";
    if (daysOverdue >= 14) return "warning";
    return "processing";
  };

  const getSeverityText = (daysOverdue) => {
    if (daysOverdue >= 30) return "Critical";
    if (daysOverdue >= 14) return "High";
    return "Moderate";
  };

  return (
    <StyledModal
      title={
        <ModalHeader>
          <WarningOutlined
            style={{
              color: "white",
              marginRight: "8px",
              filter: "brightness(0) invert(1)",
            }}
          />
          Delinquency Report
        </ModalHeader>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="90%"
      centered
    >
      <ModalContent>
        {/* Summary Statistics */}
        <SummarySection>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Total Outstanding"
                value={totalDelinquent}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#ff4d4f" }}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Overdue Accounts"
                value={(overduePayments || []).length}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: "#faad14" }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Average Days Overdue"
                value={
                  (overduePayments || []).length > 0
                    ? Math.round(
                        (overduePayments || []).reduce(
                          (sum, p) => sum + getDaysOverdue(p.dueDate),
                          0
                        ) / (overduePayments || []).length
                      )
                    : 0
                }
                prefix={<CalendarOutlined />}
                valueStyle={{ color: "#1890ff" }}
                suffix="days"
              />
            </Col>
          </Row>
        </SummarySection>

        <Divider />

        {/* Action Items */}
        <ActionSection>
          <Title level={4}>Recommended Actions</Title>
          <ActionGrid>
            <ActionCard>
              <ActionIcon>
                <ExclamationCircleOutlined />
              </ActionIcon>
              <ActionContent>
                <ActionText>
                  <ActionTitle>Send Overdue Reminders</ActionTitle>
                  <ActionDescription>
                    {(overduePayments || []).length === 0
                      ? "No overdue payments found. No reminders to send."
                      : "Send specialized overdue payment reminders with late fee calculations"}
                  </ActionDescription>
                </ActionText>
                <ActionButton
                  type="primary"
                  danger
                  loading={isSendingOverdueReminders}
                  onClick={handleSendAllOverdueReminders}
                  disabled={(overduePayments || []).length === 0}
                >
                  Send All ({(overduePayments || []).length})
                </ActionButton>
              </ActionContent>
            </ActionCard>

            <ActionCard>
              <ActionIcon>
                <FileTextOutlined />
              </ActionIcon>
              <ActionContent>
                <ActionText>
                  <ActionTitle>Generate PDF Statements</ActionTitle>
                  <ActionDescription>
                    Create detailed delinquency statements for legal purposes
                  </ActionDescription>
                </ActionText>
                <ActionButton
                  type="primary"
                  onClick={handleGenerateStatement}
                  loading={isGenerating}
                  disabled={isGenerating}
                >
                  {isGenerating ? "Generating..." : "Generate PDF"}
                </ActionButton>
              </ActionContent>
            </ActionCard>
            <ActionCard>
              <ActionIcon>
                <FileTextOutlined />
              </ActionIcon>
              <ActionContent>
                <ActionText>
                  <ActionTitle>Export to Excel (QuickBooks)</ActionTitle>
                  <ActionDescription>
                    Download as Excel spreadsheet optimized for QuickBooks
                    import
                  </ActionDescription>
                </ActionText>
                <ActionButton
                  type="default"
                  onClick={handleExportToExcel}
                  loading={isGenerating}
                  disabled={isGenerating}
                  style={{ borderColor: "#2c5f2d", color: "#2c5f2d" }}
                >
                  Export Excel
                </ActionButton>
              </ActionContent>
            </ActionCard>
          </ActionGrid>
        </ActionSection>

        <Divider />

        {/* Overdue Payments List */}
        <PaymentsSection>
          <Title level={4}>Overdue Payments Details</Title>
          {(overduePayments || []).length === 0 ? (
            <EmptyState>
              <Text type="secondary">
                No overdue payments found. Great job!
              </Text>
            </EmptyState>
          ) : (
            <List
              dataSource={overduePayments || []}
              renderItem={(payment) => {
                const daysOverdue = getDaysOverdue(payment.dueDate);
                const severity = getSeverityColor(daysOverdue);
                const severityText = getSeverityText(daysOverdue);

                return (
                  <PaymentItem>
                    <PaymentInfo>
                      <PaymentHeader>
                        <PaymentAmount>
                          ${payment.amount?.toLocaleString() || 0}
                        </PaymentAmount>
                        <Tag
                          color={
                            severity === "error"
                              ? "red"
                              : severity === "warning"
                              ? "orange"
                              : "blue"
                          }
                          style={{
                            fontWeight: 600,
                            borderRadius: "6px",
                            fontSize: "12px",
                          }}
                        >
                          {severityText}
                        </Tag>
                      </PaymentHeader>
                      <PaymentDetails>
                        <PaymentDetail>
                          <UserOutlined />
                          <Text strong>
                            {payment.tenantName || "Unknown Tenant"}
                          </Text>
                        </PaymentDetail>
                        <PaymentDetail>
                          <HomeOutlined />
                          <Text>{payment.unitName || "Unknown Door"}</Text>
                        </PaymentDetail>
                        <PaymentDetail>
                          <CalendarOutlined />
                          <Text>
                            Due:{" "}
                            {payment.dueDate
                              ?.toDate?.()
                              ?.toLocaleDateString() || "Unknown Date"}
                          </Text>
                        </PaymentDetail>
                        <PaymentDetail>
                          <ExclamationCircleOutlined />
                          <Text type="danger">{daysOverdue} days overdue</Text>
                        </PaymentDetail>
                      </PaymentDetails>
                    </PaymentInfo>
                    <PaymentActions>
                      <Button
                        icon={<MessageOutlined />}
                        loading={sendingReminderToTenant === payment.tenantId}
                        onClick={() => confirmSendReminder(payment)}
                      >
                        Regular Reminder
                      </Button>
                      <Button
                        icon={<ExclamationCircleOutlined />}
                        danger
                        loading={sendingReminderToTenant === payment.tenantId}
                        onClick={() => confirmSendOverdueReminder(payment)}
                      >
                        Overdue Reminder
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => confirmMarkPaid(payment)}
                      >
                        Mark Paid
                      </Button>
                    </PaymentActions>
                  </PaymentItem>
                );
              }}
            />
          )}
        </PaymentsSection>
      </ModalContent>
    </StyledModal>
  );
};

// Styled Components
const ActionConfirmationContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  width: 100%;
  text-align: center;

  .anticon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #000000;
    font-size: 2rem;
    line-height: 1;
  }

  .anticon svg {
    display: block;
    color: #000000;
    fill: currentColor;
  }
`;

const ActionConfirmationMessage = styled.div`
  width: 100%;
  color: var(--color-text);
  font-family: var(--font-secondary);
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: 16px;
    right: 20px;

    &:hover {
      color: rgba(255, 255, 255, 0.8) !important;
    }
  }

  .ant-modal-close .ant-modal-close-x,
  .ant-modal-close .ant-modal-close-icon,
  .ant-modal-close .anticon,
  .ant-modal-close svg,
  .ant-modal-close:hover .ant-modal-close-x,
  .ant-modal-close:hover .ant-modal-close-icon,
  .ant-modal-close:hover .anticon,
  .ant-modal-close:hover svg {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .ant-modal-body {
    padding: 24px;
    background: #fafafa;
    overflow-x: hidden;
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0px;
  }

  .ant-modal {
    margin: 20px auto;
  }

  /* Desktop/Tablet: 25% less width */
  @media (min-width: 769px) {
    .ant-modal-wrap .ant-modal {
      width: 67.5% !important;
      max-width: 67.5vw !important;
    }
    .ant-modal-content {
      width: 52rem !important;
      max-width: 100% !important;
      margin: auto;
    }
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
    }

    .ant-modal-body {
      padding: 16px;
    }

    .ant-modal-header {
      padding: 12px 16px;
    }

    .ant-modal-title {
      font-size: 16px;
    }

    .ant-modal-close {
      top: 12px;
      right: 16px;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    .ant-modal {
      max-width: 98vw;
      margin: 5px auto;
    }

    .ant-modal-body {
      padding: 12px;
    }

    .ant-modal-header {
      padding: 10px 12px;
    }

    .ant-modal-title {
      font-size: 15px;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
`;

const ModalContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: 75vh;
  }

  @media (max-width: 480px) {
    padding: 12px;
    max-height: 80vh;
  }
`;

const SummarySection = styled.div`
  --summary-number-size: 20px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-light);
    margin-bottom: 6px;
  }

  .ant-statistic-content,
  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--summary-number-size) !important;
    font-weight: 700 !important;
    color: var(--color-text) !important;
    line-height: 1.2 !important;
  }

  .ant-statistic-content-prefix {
    font-size: var(--summary-number-size) !important;
    font-weight: 700 !important;
    color: var(--color-text) !important;
    margin-right: 6px;
  }

  .ant-statistic-content-suffix {
    font-size: var(--summary-number-size) !important;
    font-weight: 700 !important;
    color: var(--color-text) !important;
    margin-left: 3px;
  }

  @media (max-width: 768px) {
    --summary-number-size: 18px;
    margin-bottom: 20px;
    padding: 16px;

    .ant-statistic-title {
      font-size: 12px;
    }

  }

  @media (max-width: 480px) {
    --summary-number-size: 16px;
    margin-bottom: 16px;
    padding: 14px;

    .ant-statistic-title {
      font-size: 11px;
    }

  }
`;

const ActionSection = styled.div`
  margin-bottom: 32px;

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    margin-bottom: 24px;

    h4 {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

const ActionGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 768px) {
    gap: 12px;
    margin-top: 12px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    margin-top: 10px;
  }
`;

const ActionCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    padding: 14px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
    flex-direction: column;
    text-align: center;
  }
`;

const ActionIcon = styled.div`
  width: 40px;
  height: 40px;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  background: none;
  box-shadow: none;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 18px;
    margin: 0 auto 6px;
  }
`;

const ActionContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
    align-items: center;
  }
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;

  @media (max-width: 480px) {
    align-items: center;
  }
`;

const ActionTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: var(--color-text);
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 3px;
  }
`;

const ActionDescription = styled.div`
  font-size: 13px;
  color: var(--color-text-light);
  margin-bottom: 10px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-bottom: 6px;
  }
`;

const ActionButton = styled(Button)`
  margin-top: 0;
  margin-left: auto;
  border-radius: 6px;
  font-weight: 600;
  height: 32px;
  padding: 0 12px;
  font-size: 13px;

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);

    &:hover {
      background: #4a7c59;
      border-color: #4a7c59;
    }

    &:disabled,
    &.ant-btn-disabled {
      background: rgba(0, 0, 0, 0.06) !important;
      border-color: #d9d9d9 !important;
      color: rgba(
        0,
        0,
        0,
        0.45
      ) !important; /* Increased from 0.25 to 0.45 for better readability */
      cursor: not-allowed !important;

      /* Override white text color from primary/danger buttons */
      span,
      .anticon {
        color: rgba(0, 0, 0, 0.45) !important;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.06) !important;
        border-color: #d9d9d9 !important;
        color: rgba(0, 0, 0, 0.45) !important;

        span,
        .anticon {
          color: rgba(0, 0, 0, 0.45) !important;
        }
      }
    }
  }

  &.ant-btn-default {
    border-color: #d9d9d9;
    color: var(--color-text);

    &:hover {
      border-color: var(--color-primary);
      color: var(--color-primary);
    }

    &:disabled,
    &.ant-btn-disabled {
      background: rgba(0, 0, 0, 0.06) !important;
      border-color: #d9d9d9 !important;
      color: rgba(
        0,
        0,
        0,
        0.45
      ) !important; /* Increased from 0.25 to 0.45 for better readability */
      cursor: not-allowed !important;

      &:hover {
        background: rgba(0, 0, 0, 0.06) !important;
        border-color: #d9d9d9 !important;
        color: rgba(0, 0, 0, 0.45) !important;
      }
    }
  }

  &.ant-btn-dangerous {
    &:disabled,
    &.ant-btn-disabled {
      background: rgba(0, 0, 0, 0.06) !important;
      border-color: #d9d9d9 !important;
      color: rgba(
        0,
        0,
        0,
        0.45
      ) !important; /* Increased from 0.25 to 0.45 for better readability */
      cursor: not-allowed !important;

      /* Override white text color from primary/danger buttons */
      span,
      .anticon {
        color: rgba(0, 0, 0, 0.45) !important;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.06) !important;
        border-color: #d9d9d9 !important;
        color: rgba(0, 0, 0, 0.45) !important;

        span,
        .anticon {
          color: rgba(0, 0, 0, 0.45) !important;
        }
      }
    }
  }

  /* Additional override for primary + dangerous combination */
  &.ant-btn-primary.ant-btn-dangerous {
    &:disabled,
    &.ant-btn-disabled {
      color: rgba(0, 0, 0, 0.45) !important;

      span,
      .anticon {
        color: rgba(0, 0, 0, 0.45) !important;
      }
    }
  }

  @media (max-width: 768px) {
    height: 30px;
    padding: 0 10px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    height: 28px;
    padding: 0 8px;
    font-size: 11px;
    width: 100%;
    margin-top: 4px;
  }
`;

const PaymentsSection = styled.div`
  margin-bottom: 24px;

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;

    h4 {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;

  @media (max-width: 768px) {
    padding: 30px 15px;
  }

  @media (max-width: 480px) {
    padding: 20px 10px;
    font-size: 14px;
  }
`;

const PaymentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin-bottom: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    padding: 14px;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 8px;
    flex-direction: column;
    gap: 10px;
  }
`;

const PaymentInfo = styled.div`
  flex: 1;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PaymentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    gap: 6px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }
`;

const PaymentAmount = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #ff4d4f;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const PaymentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 768px) {
    gap: 3px;
  }

  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const PaymentDetail = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--color-text-light);

  .anticon {
    margin-right: 6px;
    color: var(--color-text-light);
    font-size: 13px;
  }

  @media (max-width: 768px) {
    font-size: 12px;

    .anticon {
      margin-right: 5px;
      font-size: 12px;
    }
  }

  @media (max-width: 480px) {
    font-size: 11px;

    .anticon {
      margin-right: 4px;
      font-size: 11px;
    }
  }
`;

const PaymentActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 12px;

  .ant-btn {
    border-radius: 6px;
    font-weight: 600;
    height: 30px;
    padding: 0 10px;
    font-size: 12px;

    &.ant-btn-primary {
      background: var(--color-primary);
      border-color: var(--color-primary);

      &:hover {
        background: #4a7c59;
        border-color: #4a7c59;
      }
    }

    &.ant-btn-default {
      border-color: #d9d9d9;
      color: var(--color-text);

      &:hover {
        border-color: var(--color-primary);
        color: var(--color-primary);
      }
    }
  }

  @media (max-width: 768px) {
    margin-left: 10px;
    gap: 5px;

    .ant-btn {
      height: 28px;
      padding: 0 8px;
      font-size: 11px;
    }
  }

  @media (max-width: 480px) {
    margin-left: 0;
    flex-direction: row;
    gap: 6px;
    width: 100%;

    .ant-btn {
      flex: 1;
      height: 26px;
      padding: 0 6px;
      font-size: 10px;
    }
  }
`;

export default DelinquencyReportModal;
